import React, { useContext } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import 'bootstrap/dist/css/bootstrap.min.css'
import { useMutation, useQuery } from '@apollo/client'
import { SAVE_EMAIL_CONFIGURATION } from '../../../graphql/mutation'
import { toast } from 'react-toastify'
import UserContext from '../../../context/UserContext'
import { usePlan } from '../../../context/PlanContext'
import { GET_ALL_EMAIL_CONFIG_BY_BG_ACCOUNT_ID } from '../../../graphql/query'
import { parse } from 'path'

interface IProps {
  closeEmailModal: () => void
}

const EmailModal: React.FC<IProps> = ({ closeEmailModal }) => {
  const { currentPlan } = usePlan()
  const { data, loading, error } = useQuery(GET_ALL_EMAIL_CONFIG_BY_BG_ACCOUNT_ID, {
    variables: {
      input: {
        bgAccountId: currentPlan?.bgAccountId
      }
    },
    context: {
      headers: {
        'access-control-allow-methods': ''
      }
    }
  })
  const [saveEmailConfiguration] = useMutation(SAVE_EMAIL_CONFIGURATION, {
    context: {
      headers: {
        'access-control-allow-methods': 'Configuration'
      }
    }
  })

  // Initial Values
  const initialValues = {
    hostname: data?.getAllEmailonfigByBgAccountId[0]?.hostName || '',
    port: data?.getAllEmailonfigByBgAccountId[0]?.port || 0,
    username: data?.getAllEmailonfigByBgAccountId[0]?.userName || '',
    password: data?.getAllEmailonfigByBgAccountId[0]?.password || '',
    bgaccount: data?.getAllEmailonfigByBgAccountId[0]?.bgAccountId || 0,
    email: data?.getAllEmailonfigByBgAccountId[0]?.email || '',
    ssl: data?.getAllEmailonfigByBgAccountId[0]?.ssl || false,
    tls: data?.getAllEmailonfigByBgAccountId[0]?.tls || false
  }

  // Validation Schema
  const validationSchema = Yup.object({
    hostname: Yup.string().required('Hostname is required'),
    port: Yup.number().required('Port is required').typeError('Port must be a number'),
    username: Yup.string().required('Username is required'),
    password: Yup.string().required('Password is required'),
    bgaccount: Yup.string().required('BG Account is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    ssl: Yup.boolean(),
    tls: Yup.boolean()
  })

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      console.log(values)
      saveEmailConfiguration({
        variables: {
          input: {
            bgAccountId: currentPlan?.bgAccountId,
            email: values.email,
            hostName: values.hostname,
            port: values.port,
            userName: values.username,
            password: values.password,
            ssl: values.ssl,
            tls: values.tls
          }
        }
      })
        .then((res: any) => {
          closeEmailModal()
          if (
            res.data.saveEmailConfiguration.status >= 200 &&
            res.data.saveEmailConfiguration.status < 300
          ) {
            toast.success(res.data.saveEmailConfiguration.message)
          } else {
            toast.error(res.data.saveEmailConfiguration.message)
          }
        })
        .catch((err) => {
          closeEmailModal()
          toast.error('Sorry! Unexpected Error.')
          console.error(err)
        })
    }
  })

  return (
    <div className='collapse show'>
      <form className='form' onSubmit={formik.handleSubmit}>
        {formik.status && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}
        <div className='card-body p-9'>
          <div className='row mb-6'>
            <h1 className='text-center'>Email Configuration</h1>
          </div>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label required fw-semibold fs-6'>Hostname</label>

            <div className='col-lg-8 fv-row'>
              <input
                type='text'
                {...formik.getFieldProps('hostname')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.hostname && formik.errors.hostname
                  },
                  {
                    'is-valid': formik.touched.hostname && !formik.errors.hostname
                  }
                )}
                placeholder='Hostname'
              />
              {formik.touched.hostname && formik.errors.hostname && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert text-danger'>{String(formik.errors.hostname)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label required fw-semibold fs-6'>Port</label>

            <div className='col-lg-8 fv-row'>
              <input
                type='number'
                {...formik.getFieldProps('port')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.port && formik.errors.port
                  },
                  {
                    'is-valid': formik.touched.port && !formik.errors.port
                  }
                )}
                placeholder='port'
              />
              {formik.touched.port && formik.errors.port && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{Number(formik.errors.port)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-semibold fs-6'>
              <span className='required'>Email</span>
              <span className='ms-1' data-bs-toggle='tooltip' title='email'></span>
            </label>

            <div className='col-lg-8 fv-row'>
              <input
                type='text'
                {...formik.getFieldProps('email')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.email && formik.errors.email
                  },
                  {
                    'is-valid': formik.touched.email && !formik.errors.email
                  }
                )}
                placeholder='Email'
              />
              {formik.touched.email && formik.errors.email && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{String(formik.errors.email)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-semibold fs-6'>
              <span className='required'>Username</span>
              <span className='ms-1' data-bs-toggle='tooltip' title='username'></span>
            </label>

            <div className='col-lg-8 fv-row'>
              <input
                type='text'
                {...formik.getFieldProps('username')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.username && formik.errors.username
                  },
                  {
                    'is-valid': formik.touched.username && !formik.errors.username
                  }
                )}
                placeholder='Username'
              />
              {formik.touched.username && formik.errors.username && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{String(formik.errors.username)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-semibold fs-6'>
              <span className='required'>Password</span>
              <span className='ms-1' data-bs-toggle='tooltip' title='Password'></span>
            </label>

            <div className='col-lg-8 fv-row'>
              <input
                type='text'
                {...formik.getFieldProps('password')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.password && formik.errors.password
                  },
                  {
                    'is-valid': formik.touched.password && !formik.errors.password
                  }
                )}
                placeholder='Password'
              />
              {formik.touched.password && formik.errors.password && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{String(formik.errors.password)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-semibold fs-6'>
              <span>SSL</span>
            </label>

            <div className='col-lg-8 fv-row d-flex align-items-center'>
              <input
                type='checkbox'
                {...formik.getFieldProps('ssl')}
                className='form-check-input'
              />
              {formik.touched.ssl && formik.errors.ssl && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{Boolean(formik.errors.ssl)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-semibold fs-6'>
              <span>TLS</span>
            </label>

            <div className='col-lg-8 fv-row d-flex align-items-center'>
              <input
                type='checkbox'
                {...formik.getFieldProps('tls')}
                className='form-check-input'
              />
              {formik.touched.tls && formik.errors.tls && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{Boolean(formik.errors.tls)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='d-flex justify-content-end mt-4'>
            <button type='button' className='btn btn-secondary me-4' onClick={closeEmailModal}>
              Cancel
            </button>
            <button type='submit' className='btn btn-sm btn-primary' disabled={formik.isSubmitting}>
              Save
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default EmailModal
